import React, { useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { usePoll } from "@/api/polls";
import { useVotePoll } from "@/api/polls/hooks/useVotePoll";
import { useModalContext } from "@/contexts/modal-context";
import { useLoggedIn } from "@/hooks";

import { Stack, Text } from "../ui";

import PollFooter from "./poll-footer";
import PollOption from "./poll-option";

import type { Status } from "@/schemas";

export type Selected = Record<number, boolean>;

interface PollProps {
  id: string;
  status: Status;
}

const messages = defineMessages({
  multiple: {
    id: "poll.choose_multiple",
    defaultMessage: "Choose as many as you'd like.",
  },
});

const Poll = ({ id, status }: PollProps) => {
  const intl = useIntl();
  const { openModal } = useModalContext();

  const votePoll = useVotePoll();

  const { isLoggedIn } = useLoggedIn();
  const { poll, refetch } = usePoll(id);

  const [selected, setSelected] = useState({} as Selected);
  const [animationKey, setAnimationKey] = useState(0);

  const openUnauthorizedModal = () => openModal("UNAUTHORIZED");

  const handleRefresh = async () => {
    await refetch();
    setAnimationKey((prev) => prev + 1);
  };

  const handleVote = (selectedId: number) => {
    votePoll.mutate({
      statusId: status.id,
      pollId: id,
      choices: [String(selectedId)],
    });
  };

  const toggleOption = (value: number) => {
    if (isLoggedIn) {
      if (poll?.multiple) {
        const tmp = { ...selected };
        if (tmp[value]) {
          delete tmp[value];
        } else {
          tmp[value] = true;
        }
        setSelected(tmp);
      } else {
        const tmp: Selected = {};
        tmp[value] = true;
        setSelected(tmp);
        handleVote(value);
      }
    } else {
      openUnauthorizedModal();
    }
  };

  if (!poll) return null;

  const showResults = poll.voted || poll.expired;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {!showResults && poll.multiple && (
        <Text theme="muted" size="sm">
          {intl.formatMessage(messages.multiple)}
        </Text>
      )}

      <Stack space={4} className="mt-4">
        <Stack space={2}>
          {poll.options.map((option, i) => (
            <PollOption
              key={`${i}-${animationKey}`}
              poll={poll}
              option={option}
              index={i}
              showResults={showResults}
              active={!!selected[i]}
              onToggle={toggleOption}
            />
          ))}
        </Stack>

        <PollFooter
          statusId={status.id}
          poll={poll}
          selected={selected}
          onRefresh={handleRefresh}
        />
      </Stack>
    </div>
  );
};

export default Poll;
